import {Component, Input, OnInit} from '@angular/core';
import {ContactDetailsResponse, ReferenceBookResponse, ReferenceBookType} from '@npck/af-frontend-api';
import {formatDateTimeFromServerToUi, RouterService} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {forkJoin} from 'rxjs';
import {DictionaryHolderService, findValueInDictionary, resolveRefName} from '../../../../../../../domain/dictionary-holder.service';
import {Dictionaries, FieldBuilderData, FieldBuilderService, FieldInfo} from '../../../../../../../domain/field-builder.service';
import {formatMsisdnDisplay} from '../../../../../../../domain/phone-utils';
import {Path} from '../../../../../../../path';
import {OTHER_VALUE} from '../../../../../create-edit/contact-details-step/contact-details-step.component';

@Component({
    selector: 'af-contact-details-view-tab',
    templateUrl: 'contact-details-view-tab.component.html',
    styleUrl: 'contact-details-view-tab.component.scss',
    standalone: false,
})
export class ContactDetailsViewTabComponent implements OnInit {
    @Input() contactDetails: ContactDetailsResponse;
    fields: FieldInfo[] = [];

    private readonly fieldNames = [
        'contactType', 'msisdn', 'contactDetail', 'messengerContactType',
        'fraudsterRole', 'contactTimeframe',
        'contactReason', 'victimActions', 'contactDate', 'victimAppealDate',
        'ipFrom', 'ipTo', 'suspiciousLinks', 'details',
    ];

    constructor(
        private dictionaryHolderService: DictionaryHolderService,
        private fieldBuilderService: FieldBuilderService,
        private routerService: RouterService,
    ) {
    }

    ngOnInit(): void {
        forkJoin([
            this.dictionaryHolderService.getReference(ReferenceBookType.CONTACT_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.CONTACT_TIMEFRAME),
            this.dictionaryHolderService.getReference(ReferenceBookType.CONTACT_REASON),
            this.dictionaryHolderService.getReference(ReferenceBookType.VICTIM_ACTION),
            this.dictionaryHolderService.getReference(ReferenceBookType.FRAUDSTER_ROLE),
            this.dictionaryHolderService.getReference(ReferenceBookType.CALL_CONTACT_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.MESSENGER_CONTACT_SUBTYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.EMAIL_CONTACT_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.SOCIAL_CONTACT_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.INTERNET_CONTACT_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.MESSENGER_CONTACT_TYPE),
        ]).subscribe(([contactTypes, timeframes, reasons, actions, roles,
                          callContactTypes, messengerContactSubtypes, emailContactTypes,
                          socialContactTypes, internetContactTypes, messengerContactTypes]) => {
            const dict: Dictionaries = {
                contactTypes, timeframes, reasons, actions, roles,
                callContactTypes, messengerContactSubtypes, emailContactTypes,
                socialContactTypes, internetContactTypes,
            };
            const cd = this.contactDetails;

            this.fields = this.fieldBuilderService.buildFields(<FieldBuilderData>{
                fieldNames: this.fieldNames,
                data: cd,
                dictionaries: dict,
                customValueMapper: this.buildCustomValueMapper(cd, dict, messengerContactTypes),
            }).filter(field => field.fieldValue && field.fieldValue !== '-');

            if (cd.msisdnId) {
                const msisdnField = this.fields.find(f => f.fieldName === 'msisdn');
                if (msisdnField) {
                    msisdnField.click = () => this.routerService.navigate(`${Path.SUBJECTS}/msisdn/${cd.msisdnId}`);
                }
            }
        });
    }

    private buildCustomValueMapper(
        cd: ContactDetailsResponse, dict: Dictionaries, messengerContactTypes: ReferenceBookResponse[],
    ): Record<string, (value: string) => string> {
        return {
            contactType: (v) => findValueInDictionary(dict.contactTypes, v, 'name_ru') ?? v,
            messengerContactType: (v) => this.resolveEnumOrOther(messengerContactTypes, v, cd.messengerContactTypeOther),
            contactDetail: () => this.resolveDetail(cd, dict),
            fraudsterRole: (v) => this.resolveEnumOrOther(dict.roles, v, cd.fraudsterRoleOther),
            contactTimeframe: (v) => findValueInDictionary(dict.timeframes, v, 'name_ru') ?? v,
            contactReason: (v) => this.resolveEnumOrOther(dict.reasons, v, cd.contactReasonOther),
            victimActions: () => cd.victimActions
                ?.map(a => a === OTHER_VALUE ? this.formatOther(cd.victimActionOther) : resolveRefName(dict.actions, a))
                .filter(Boolean).join(', '),
            contactDate: (v) => v ? formatDateTimeFromServerToUi(dayjs(v)) : null,
            victimAppealDate: (v) => v ? formatDateTimeFromServerToUi(dayjs(v)) : null,
            msisdn: (v) => v ? formatMsisdnDisplay(v) : null,
            ipFrom: () => cd.ipFrom ? (cd.portFrom ? `${cd.ipFrom}:${cd.portFrom}` : cd.ipFrom) : null,
            ipTo: () => cd.ipTo ? (cd.portTo ? `${cd.ipTo}:${cd.portTo}` : cd.ipTo) : null,
            suspiciousLinks: () => cd.suspiciousLinks?.length ? cd.suspiciousLinks.join('\n') : null,
            details: (v) => v,
        };
    }

    private resolveDetail(cd: ContactDetailsResponse, dict: Dictionaries): string {
        const typeResolverMap: Record<string, ContactDetailResolver> = {
            CALL: {value: cd.callContactType, otherValue: cd.callContactTypeOther, dictionary: dict.callContactTypes},
            MESSENGER: {
                value: cd.messengerContactSubtype, otherValue: cd.messengerContactSubtypeOther,
                dictionary: dict.messengerContactSubtypes,
            },
            EMAIL: {value: cd.emailContactType, otherValue: cd.emailContactTypeOther, dictionary: dict.emailContactTypes},
            SOCIAL: {value: cd.socialContactType, otherValue: cd.socialContactTypeOther, dictionary: dict.socialContactTypes},
            INTERNET: {
                value: cd.internetContactType, otherValue: cd.internetContactTypeOther,
                dictionary: dict.internetContactTypes,
            },
        };
        const resolver = typeResolverMap[cd.contactType];
        if (!resolver?.value) {
            return null;
        }
        return this.resolveEnumOrOther(resolver.dictionary, resolver.value, resolver.otherValue);
    }

    private resolveEnumOrOther(
        dictionary: ReferenceBookResponse[], value: string, otherValue: string,
    ): string {
        if (!value) {
            return null;
        }
        return value === OTHER_VALUE ? this.formatOther(otherValue) : resolveRefName(dictionary, value);
    }

    private formatOther(value: string): string {
        return value ? `Иное: ${value}` : null;
    }
}

interface ContactDetailResolver {
    value: string;
    otherValue: string;
    dictionary: ReferenceBookResponse[];
}
